import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
/* actions */
import { addServices } from '../../redux/actions/services';
import { getAllCategories } from '../../redux/actions/categories';
/* components */
import ErrorMsgField from '../../components/ErrorMsgField';
import AlertMsg from '../../components/AlertMsg';
import FormFooter from '../../components/FormFooter';
import FormHeader from '../../components/FormHeader';
import MultiSelect from '../../components/MultiSelect';
import ModalAddCategory from '../../components/ModalAddCategory';
/* form-validation */
import schema from '../../form-validation/service';
/* utils */
import sortable from '../../utils/sortable';
import {
  handleMultiSelect,
  handleMultiDeselect
} from '../../utils/multiSelect';
import handleChangeDefault from '../../utils/handleChangeDefault';
import handleChangeCurrency from '../../utils/handleChangeCurrency';
import formatCode from '../../utils/formatCode';

function Add () {
  const dispatch = useDispatch();
  const submitButton = useRef();
  const { register, handleSubmit, errors, reset } = useForm({
    validationSchema: schema,
    reValidateMode: 'onSubmit'
  });
  const sessionUser = useSelector(state => state.authentications.user);
  const company = sessionUser.company._id;
  const services = useSelector(state => state.services);
  const INITIAL_VALUES = {
    code: '',
    name: '',
    saleValue: '0,00',
    maximumDiscount: '0,00',
    description: '',
    categories: [],
    company
  };
  const categories = useSelector(state => state.categories.data);
  const [data, setData] = useState(INITIAL_VALUES);
  const [categoriesOptions, setCategoriesOptions] = useState({
    categories: []
  });
  const [ctgrs, setCtgrs] = useState([]);
  const [showModalAddCategory, setShowModalAddCategory] = useState(false);

  useEffect(() => {
    dispatch(getAllCategories({ company }));
  }, [dispatch]);

  useEffect(() => {
    if (categories.length) {
      const ctgrs = categories
        .map(category => ({
          sortValue: category.name,
          value: category._id,
          text: category.name
        }))
        .sort(sortable);
      setCtgrs([...ctgrs]);
    }
  }, [categories, setCtgrs]);

  useEffect(() => {
    setData({ ...data, code: formatCode(services.data.length + 1) });
  }, [services.data, data.code]);

  const submit = () => {
    const resetForm = () => {
      reset();
      setData({ ...INITIAL_VALUES });
      Object.keys(categoriesOptions).forEach(key =>
        setCategoriesOptions({ ...categoriesOptions, [key]: [] })
      );
    };
    const { saleValue, maximumDiscount } = data;
    const newData = {
      ...data,
      saleValue: parseFloat(saleValue.replace(/,/g, '.')),
      maximumDiscount: parseFloat(maximumDiscount.replace(/,/g, '.'))
    };
    dispatch(addServices(newData, resetForm, submitButton.current));
  };

  return (
    <div className="container-fluid">
      <ModalAddCategory
        showModal={showModalAddCategory}
        handleModalClose={() => setShowModalAddCategory(false)}
      />
      {services.addSuccess && (
        <AlertMsg type="success" msg="Serviço cadastrado com sucesso" />
      )}
      {services.errorContent &&
        !services.errorContent.message.includes('400') &&
        services.addError && (
        <AlertMsg type="danger" msg="Falha durante cadastro do serviço" />
      )}
      {services.errorContent &&
        services.errorContent.message.includes('400') &&
        services.addError && (
        <AlertMsg
          type="danger"
          msg="Um serviço com o nome informado já está cadastrado"
        />
      )}
      {!services.addSuccess && !services.addError && (
        <AlertMsg type="info" msg="Área cadastro de serviços" />
      )}
      <FormHeader title="Cadastrar Serviço" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="code">
                Código<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="code"
                name="code"
                autoComplete="off"
                disabled={true}
                value={data.code}
                ref={register}
              />
              {errors.code && errors.code.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="name">
                Nome<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                autoComplete="off"
                value={data.name}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.name && errors.name.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="saleValue">
                R$ Valor de Venda<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="saleValue"
                name="saleValue"
                number="on"
                value={data.saleValue}
                autoComplete="off"
                onChange={evt => handleChangeCurrency(evt, data, setData)}
                ref={register}
              />
              {errors.saleValue && errors.saleValue.type === 'currency' && (
                <ErrorMsgField type="currency" />
              )}
              {errors.saleValue && errors.saleValue.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
              {errors.saleValue && errors.saleValue.type === 'min' && (
                <ErrorMsgField type="min" />
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="maximumDiscount">
                % Desconto Máx.<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="maximumDiscount"
                name="maximumDiscount"
                value={data.maximumDiscount}
                autoComplete="off"
                onChange={evt => handleChangeCurrency(evt, data, setData)}
                ref={register}
              />
              {errors.maximumDiscount &&
                errors.maximumDiscount.type === 'currency' && (
                <ErrorMsgField type="currency" />
              )}
              {errors.maximumDiscount &&
                errors.maximumDiscount.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="description">
            Descrição<span className="field-required">*</span>
          </label>
          <textarea
            className="form-control"
            id="description"
            name="description"
            rows="3"
            value={data.description}
            onChange={evt => handleChangeDefault(evt, data, setData)}
            ref={register}
          ></textarea>
          {errors.description && errors.description.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <div>
          <button
            type="button"
            className="btn btn-success"
            title="Adicionar Categoria"
            onClick={() => setShowModalAddCategory(true)}
          >
            <i className="fa fa-plus" />
            &nbsp;Categoria
          </button>
        </div>
        <MultiSelect
          avaliableOptions={ctgrs}
          selectedOptions={categoriesOptions.categories}
          handleMultiDeselect={options =>
            handleMultiDeselect(
              options,
              'categories',
              categoriesOptions,
              setCategoriesOptions,
              data,
              setData
            )
          }
          handleMultiSelect={options =>
            handleMultiSelect(
              options,
              'categories',
              categoriesOptions,
              setCategoriesOptions,
              data,
              setData
            )
          }
          valueProp="value"
          textProp="text"
          title="Categorias"
          fieldRequired={true}
          name="categories"
          ref={register}
          error={!!errors.categories}
        />
        <FormFooter url="/services" reference={submitButton} />
      </form>
    </div>
  );
}

export default Add;
